import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Lock, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';

function formatEntryDate(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PublicProfilePage = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setProfile(null);
      try {
        const data = await apiFetch(
          `/api/users/${encodeURIComponent(username || '')}/profile`
        );
        if (!cancelled) setProfile(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.message || 'Could not load profile');
          setProfile(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-text-secondary text-sm">
        Loading profile…
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Link
          to="/orbit"
          className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold-muted mb-6"
        >
          <ArrowLeft size={16} />
          Back to Orbit
        </Link>
        <p className="text-text-secondary">Profile not found or unavailable.</p>
      </div>
    );
  }

  const stats = profile.stats;
  const chapter = profile.current_chapter;
  const recent = profile.recent_entries || [];

  return (
    <div className="max-w-2xl mx-auto p-6 lg:p-8" data-testid="public-profile-page">
      <Link
        to="/orbit"
        className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold-muted mb-6"
      >
        <ArrowLeft size={16} />
        Back to Orbit
      </Link>

      <div className="bg-bg-surface rounded-2xl border border-border shadow-sm p-6 lg:p-8">
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-text-primary">{profile.name}</h1>
            {profile.username ? (
              <p className="text-gold font-mono text-sm mt-1">@{profile.username}</p>
            ) : null}
          </div>
          {profile.is_self ? (
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-sage-light text-sage">
              You
            </span>
          ) : profile.journey_public ? (
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-gold-light text-gold">
              Public journey
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full bg-bg-surface-alt text-text-secondary">
              <Lock size={12} />
              Private journey
            </span>
          )}
          </div>
          {profile.is_self && !profile.journey_public ? (
            <p className="text-xs text-text-tertiary">
              Stats below are visible only to you until you turn on Public Journey in Settings.
            </p>
          ) : null}
        </div>

        {profile.position ? (
          <p className="text-text-secondary text-sm mb-2">{profile.position}</p>
        ) : null}
        {profile.bio ? (
          <p className="text-text-primary leading-relaxed mb-6">&ldquo;{profile.bio}&rdquo;</p>
        ) : null}

        {!profile.journey_public && !profile.is_self ? (
          <p className="text-sm text-text-tertiary border-t border-border pt-6">
            This Daypopper has not enabled a public journey. You can still connect in Orbit using
            their username.
          </p>
        ) : null}

        {stats ? (
          <div className="border-t border-border pt-6 space-y-6">
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">
                Snapshot
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl bg-bg-surface-alt p-3">
                  <div className="text-2xl font-bold text-text-primary">{stats.entries}</div>
                  <div className="text-xs text-text-tertiary">Entries</div>
                </div>
                <div className="rounded-xl bg-bg-surface-alt p-3">
                  <div className="text-2xl font-bold text-text-primary">{stats.goals_achieved}</div>
                  <div className="text-xs text-text-tertiary">Goals achieved</div>
                </div>
                <div className="rounded-xl bg-bg-surface-alt p-3">
                  <div className="text-2xl font-bold text-text-primary">{stats.lessons}</div>
                  <div className="text-xs text-text-tertiary">Lessons</div>
                </div>
              </div>
            </div>

            {chapter ? (
              <div>
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2 flex items-center gap-1">
                  <MapPin size={14} />
                  Current chapter
                </p>
                <p className="font-medium text-text-primary">{chapter.title}</p>
                {chapter.theme ? (
                  <p className="text-sm text-text-secondary mt-1">{chapter.theme}</p>
                ) : null}
              </div>
            ) : null}

            {recent.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                  Recent activity
                </p>
                <p className="text-xs text-text-tertiary mb-3">
                  Titles and types only — full entries stay private.
                </p>
                <ul className="space-y-2">
                  {recent.map((row, i) => (
                    <li
                      key={`${row.date}-${row.title}-${i}`}
                      className="text-sm border border-border rounded-lg px-3 py-2 flex flex-wrap gap-x-2 gap-y-1"
                    >
                      <span className="text-text-tertiary">{formatEntryDate(row.date)}</span>
                      <span className="text-text-primary font-medium">{row.title || 'Untitled'}</span>
                      <span className="text-text-secondary capitalize text-xs">{row.entry_type}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PublicProfilePage;
